import type { Session } from "@supabase/supabase-js";
import { resolveAccess } from "../auth/access";
import type { Database, Json } from "../types/database";
import type {
  AuditRepository,
  AuthorizationRepository,
  ObservabilityRepository,
  Profile,
  ProfileRepository,
  RepositoryContext,
  Role,
} from "./interfaces";

export class SupabaseProfileRepository implements ProfileRepository {
  constructor(private readonly context: RepositoryContext) {}

  async getCurrentProfile(session: Session): Promise<Profile | null> {
    const { data, error } = await this.context.supabase
      .from("profiles")
      .select(
        "id,email,display_name,avatar_url,is_authorized,status,created_at,updated_at",
      )
      .eq("id", session.user.id)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data;
  }
}

export class SupabaseAuthorizationRepository implements AuthorizationRepository {
  constructor(private readonly context: RepositoryContext) {}

  async getCurrentRoles(session: Session): Promise<Role[]> {
    const { data, error } = await this.context.supabase
      .from("user_roles")
      .select("roles(code,name)")
      .eq("user_id", session.user.id);

    if (error) {
      throw error;
    }

    return ((data ?? []) as Array<{ roles: Role | null }>)
      .map((row) => row.roles)
      .filter((role): role is Role => Boolean(role));
  }

  evaluateOwnerAccess(
    session: Session | null,
    profile: Profile | null,
    roles: Role[],
  ) {
    return resolveAccess(session, profile, roles);
  }
}

export class SupabaseAuditRepository implements AuditRepository {
  constructor(private readonly context: RepositoryContext) {}

  async recordAuthEvent(
    eventType: string,
    eventStatus: string,
    metadata: Json = {},
  ): Promise<void> {
    const { error } = await (
      this.context.supabase.rpc as unknown as (
        fn: "record_auth_event",
        args: { event_type: string; metadata: Json },
      ) => Promise<{ error: Error | null }>
    )("record_auth_event", {
      event_type: eventType,
      metadata: {
        eventStatus,
        ...(metadata && typeof metadata === "object" && !Array.isArray(metadata)
          ? metadata
          : { value: metadata }),
      } as Json,
    });

    if (error) {
      throw error;
    }
  }
}

export class SupabaseObservabilityRepository implements ObservabilityRepository {
  constructor(private readonly context: RepositoryContext) {}

  async persistSystemEvent(
    event: Database["public"]["Tables"]["system_events"]["Insert"],
  ): Promise<void> {
    const { error } = await (
      this.context.supabase.from("system_events").insert as unknown as (
        value: Database["public"]["Tables"]["system_events"]["Insert"],
      ) => Promise<{ error: Error | null }>
    )(event);

    if (error) {
      throw error;
    }
  }
}

export function createSupabaseRepositories(context: RepositoryContext) {
  return {
    profileRepository: new SupabaseProfileRepository(context),
    authorizationRepository: new SupabaseAuthorizationRepository(context),
    auditRepository: new SupabaseAuditRepository(context),
    observabilityRepository: new SupabaseObservabilityRepository(context),
  };
}
